import React from 'react';
import { CareSectionProps } from '../../../types/health-tools';
import HealthCard from '../ui/HealthCard';
import { SkeletonHealthCardsGrid } from '../ui/SkeletonHealthCard';
import { Stethoscope, Clock, User, Pill, Target, FileHeart } from 'lucide-react';
import ProgressBar from '../ui/ProgressBar';

/**
 * Care Section Component
 * 
 * Displays care-related information including care recommendations,
 * care plan (conditions, medications, goals), and healthcare providers
 * 
 * @param careRecommendations - Care recommendations data
 * @param carePlan - Care plan data including conditions, medications, and goals
 * @param providers - Healthcare providers data
 * @param isLoading - Whether data is currently loading
 * @param className - Additional CSS classes
 */
const CareSection: React.FC<CareSectionProps> = ({
  careRecommendations,
  carePlan,
  providers,
  isLoading = false,
  className = ''
}) => {
  // If loading, show skeleton components
  if (isLoading) {
    return (
      <div className={`space-y-6 ${className}`}>
        <SkeletonHealthCardsGrid count={3} columns={3} />
      </div>
    );
  }

  // Helper function to format date
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return dateString;
    }
  };

  // Helper function to get urgency color
  const getUrgencyColor = (urgency: string): string => {
    switch (urgency.toLowerCase()) {
      case 'high':
        return 'var(--status-error)';
      case 'medium':
        return 'var(--status-warning)';
      case 'low':
        return 'var(--status-info)';
      default:
        return 'var(--text-secondary)';
    }
  };

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Care Recommendations */}
      <HealthCard
        title="Care Recommendations"
        icon={Stethoscope}
      >
        <div className="space-y-4 py-2">
          {careRecommendations.length > 0 ? (
            careRecommendations.map((recommendation, index) => (
              <div key={index} className="bg-[var(--background-surface)] rounded-lg p-3">
                <div className="flex justify-between items-start mb-1">
                  <span className="font-medium text-[var(--text-primary)]">{recommendation.title}</span>
                  <span 
                    className="text-xs px-2 py-0.5 rounded-full"
                    style={{ 
                      backgroundColor: `color-mix(in srgb, ${getUrgencyColor(recommendation.urgency)} 15%, transparent)`,
                      color: getUrgencyColor(recommendation.urgency)
                    }}
                  >
                    {recommendation.urgency} Priority
                  </span>
                </div>
                <p className="text-sm text-[var(--text-secondary)] mb-2">{recommendation.description}</p>
                <div className="flex justify-between items-center text-xs">
                  <span className="text-[var(--text-tertiary)]">{recommendation.category}</span>
                  <div className="flex items-center">
                    <Clock className="h-3 w-3 text-[var(--text-tertiary)] mr-1" />
                    <span className="text-[var(--text-tertiary)]">Due: {formatDate(recommendation.dueDate)}</span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <p className="text-center text-[var(--text-secondary)] py-4">
              No care recommendations at this time.
            </p>
          )}
        </div>
      </HealthCard>

      {/* Care Plan */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Medical Conditions & Medications */}
        <HealthCard
          title="Care Plan"
          icon={FileHeart}
        >
          <div className="space-y-4 py-2">
            <div>
              <h3 className="text-sm font-medium mb-2 flex items-center">
                <span className="text-[var(--text-primary)]">Medical Conditions</span>
              </h3>
              <div className="space-y-2">
                {carePlan.conditions.map((condition, index) => (
                  <div key={index} className="bg-[var(--background-surface)] p-2 rounded-lg">
                    <div className="flex justify-between">
                      <span className="text-sm text-[var(--text-primary)]">{condition.name}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-[var(--background-inset)] text-[var(--text-secondary)]">
                        {condition.status}
                      </span>
                    </div>
                    <p className="text-xs text-[var(--text-tertiary)]">
                      Last reviewed: {formatDate(condition.lastReviewed)}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-sm font-medium mb-2 flex items-center">
                <Pill className="h-4 w-4 text-[var(--brand-primary)] mr-1" />
                <span className="text-[var(--text-primary)]">Medications</span>
              </h3>
              <div className="space-y-2">
                {carePlan.medications.map((medication, index) => (
                  <div key={index} className="bg-[var(--background-surface)] p-2 rounded-lg">
                    <div className="flex justify-between">
                      <span className="text-sm font-medium text-[var(--text-primary)]">{medication.name}</span>
                      <span className="text-xs text-[var(--text-secondary)]">{medication.dosage}</span>
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-[var(--text-tertiary)]">{medication.frequency}</span>
                      <span className="text-xs text-[var(--text-tertiary)]">{medication.purpose}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </HealthCard>

        {/* Health Goals & Providers */}
        <div className="space-y-6">
          {/* Health Goals */}
          <HealthCard
            title="Health Goals"
            icon={Target}
          >
            <div className="space-y-4 py-2">
              {carePlan.goals.map((goal, index) => (
                <div key={index} className="bg-[var(--background-surface)] p-3 rounded-lg">
                  <p className="text-sm font-medium text-[var(--text-primary)] mb-1">{goal.description}</p>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-[var(--text-secondary)]">Progress</span>
                    <span className="text-[var(--text-secondary)]">{goal.progress}% of {goal.target}</span>
                  </div>
                  <ProgressBar 
                    value={goal.progress} 
                    max={100} 
                    color="var(--brand-primary)" 
                    height={8}
                  />
                </div>
              ))}
            </div>
          </HealthCard>

          {/* Healthcare Providers */}
          <HealthCard
            title="Your Care Team"
            icon={User}
          >
            <div className="space-y-3 py-2">
              {providers.map((provider, index) => (
                <div key={index} className="bg-[var(--background-surface)] p-3 rounded-lg">
                  <p className="text-sm font-medium text-[var(--text-primary)]">{provider.name}</p>
                  <p className="text-xs text-[var(--text-secondary)] mb-1">{provider.specialty}</p>
                  <div className="flex justify-between text-xs text-[var(--text-tertiary)]">
                    <span>Last visit: {formatDate(provider.lastVisit)}</span>
                    {provider.nextVisit && (
                      <span>Next: {formatDate(provider.nextVisit)}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </HealthCard>
        </div>
      </div>
    </div>
  );
};

export default CareSection; 